import { useEffect, useRef, useState } from 'react';
import { useDatasetFormData } from '../../../../hooks/context/useDatasetFormData';
import { useRequestDataContext } from '../../../../hooks/context/useRequestDataContext';
import { useResponseActionsContext } from '../../../../hooks/context/useResponseActionsContext';
import { isEmptyValue } from '../../../../utils/managerUtils';
import { findDynamicListControlItems } from '../utils/findDynamicListControlItems';
import { ControlItemValue, StaticControlItem } from '../../../../types/types';
import { fetchControlPrefetchResult } from '../utils/fetchControlPrefetchResult';
import { processPrefetchResults } from '../utils/processPrefetchResults';

// Prefetches apiMethod list-control (radioBtn, combobox) options at mount to seed defaults for inactive-tab fields; re-applies on each formData change because init runs setFormData twice.
export const useFormOptionsPrefetch = (): void => {
  const { form, formData, patchFormData, setPrefetchedFormOptions } =
    useDatasetFormData();
  const requestData = useRequestDataContext();
  const { getResponseActionHandler } = useResponseActionsContext();

  // Cached first-option defaults keyed by field name. Set once after the
  // parallel fetch resolves; replayed into formData by the second effect.
  const [defaults, setDefaults] = useState<Record<
    string,
    ControlItemValue
  > | null>(null);
  const [prefetchedOptions, setPrefetchedOptions] = useState<Record<
    string,
    StaticControlItem[]
  > | null>(null);
  const hasFetchedRef = useRef<boolean>(false);

  // Effect 1: fetch options once form & requestData are available.
  useEffect(() => {
    if (hasFetchedRef.current) return;
    if (!form || !requestData) return;

    const dynamicListControls = findDynamicListControlItems(form);

    // No dynamic list controls — mark prefetch done immediately so GridInput skips loading state
    if (dynamicListControls.length === 0) {
      setPrefetchedFormOptions({});
      return;
    }

    // Mark before fetch to prevent re-trigger when requestData re-creates
    // due to formData updates inside DatasetFormDataContextProvider.
    hasFetchedRef.current = true;

    const dynamicListControlPromises = dynamicListControls.map((control) =>
      fetchControlPrefetchResult(control, requestData, getResponseActionHandler)
    );

    Promise.all(dynamicListControlPromises).then((results) => {
      const { collected, optionsMap } = processPrefetchResults(results);
      if (Object.keys(collected).length > 0) {
        setDefaults(collected);
      }
      setPrefetchedOptions(optionsMap);
      // Always publish the map (even if empty due to all-failed fetches) to signal done
      setPrefetchedFormOptions(optionsMap);
    });
  }, [form, requestData, setPrefetchedFormOptions]);

  // Re-applies cached defaults on every formData change so later setFormData calls don't wipe them.
  useEffect(() => {
    if (!defaults) return;
    if (!formData) return;

    const patch: Record<string, unknown> = {};
    Object.keys(defaults).forEach((name) => {
      const currentValue = formData[name];
      const fieldOptions = prefetchedOptions?.[name];
      const valueNotInOptions =
        fieldOptions != null &&
        !isEmptyValue(currentValue) &&
        !fieldOptions.some((o) => o.value === currentValue);
      if (isEmptyValue(currentValue) || valueNotInOptions) {
        patch[name] = defaults[name];
      }
    });

    if (Object.keys(patch).length > 0) {
      patchFormData(patch);
    }
  }, [defaults, formData, patchFormData, prefetchedOptions]);
};
